import createTheme from '@material-ui/core/styles/createTheme';

declare module '@material-ui/core/styles/createPalette' {
  // tslint:disable-next-line:interface-name
  interface PaletteOptions {
    access?: PaletteColorOptions;
  }
}

const theme = createTheme({
  breakpoints: {
    keys: ['xs', 'sm', 'md', 'lg', 'xl'],
    values: {
      xs: 0,
      sm: 375,
      md: 768,
      lg: 992,
      xl: 1440,
    },
  },
  palette: {
    primary: {
      main: '#8BB90A',
      dark: '#6F9F0A',
    },
    secondary: {
      main: '#166472',
      dark: '#12525D',
    },
    success: {
      main: '#8BB90A',
    },
    error: {
      main: '#CD4A43',
      dark: '#B23F3A',
    },
    text: {
      primary: '#364754',
      secondary: '#63706E',
      disabled: '#63706E',
    },
    grey: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#eeeeee',
      300: '#e0e0e0',
      400: '#bdbdbd',
      500: '#9e9e9e',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
      A100: '#d5d5d5',
      A200: '#aaaaaa',
      A400: '#303030',
      A700: '#616161',
    },
    action: {
      active: 'rgba(0, 0, 0, 0.54)',
      hover: 'rgba(0, 0, 0, 0.08)',
      hoverOpacity: 0.08,
      selected: 'rgba(0, 0, 0, 0.14)',
      disabled: 'rgba(54, 71, 84, 0.38)',
      disabledBackground: '#D9E3E2',
    },
  },
});

const fontFamily = ['Montserrat', 'Droid Serif'].join(',');

theme.typography = {
  fontFamily,
  fontSize: 16,
  body1: {
    fontWeight: 400,
    lineHeight: 1.5,
    fontSize: '15px',
    color: theme.palette.text.primary,
    [theme.breakpoints.down('sm')]: {
      fontSize: '13px',
      lineHeight: 1.4,
    },
  },
  body2: {
    fontWeight: 500,
    lineHeight: 1.5,
    fontSize: '13px',
    color: theme.palette.text.primary,
    [theme.breakpoints.down('sm')]: {
      fontSize: '13px',
      lineHeight: 1.4,
    },
  },
  button: {
    fontFamily,
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: 1.4,
  },
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: 700,
  h1: {
    fontWeight: 400,
    fontSize: '44px',
    color: theme.palette.text.primary,
    lineHeight: 1.4,
    [theme.breakpoints.down('sm')]: {
      ontSize: '28px',
      fontWeight: 500,
    },
  },
  h2: {
    fontWeight: 400,
    fontSize: '30px',
    color: theme.palette.text.primary,
    lineHeight: 1.4,
    [theme.breakpoints.down('sm')]: {
      fontSize: '24px',
      fontWeight: 500,
    },
  },
  h3: {
    fontWeight: 500,
    fontSize: '22px',
    color: theme.palette.text.primary,
    lineHeight: 1.4,
    [theme.breakpoints.down('sm')]: {
      fontSize: '21px',
    },
  },
  h4: {
    fontWeight: 500,
    fontSize: '21px',
    color: theme.palette.text.primary,
    lineHeight: 1.4,
    [theme.breakpoints.down('sm')]: {
      fontSize: '18px',
      fontWeight: 600,
    },
  },
  h5: {
    fontWeight: 600,
    fontSize: '16px',
    color: theme.palette.text.primary,
    lineHeight: 1.4,
  },
  h6: theme.typography.h6,
  overline: theme.typography.overline,
  pxToRem: theme.typography.pxToRem,
  subtitle1: theme.typography.subtitle1,
  subtitle2: theme.typography.subtitle2,
  caption: {
    fontWeight: 500,
    fontSize: '12px',
    color: theme.palette.text.primary,
  },
};

theme.overrides = {
  MuiButton: {
    root: {
      fontWeight: 600,
      borderRadius: '6px',
      padding: '12px 16px',
      minWidth: '80px',
      [theme.breakpoints.only('xs')]: {
        width: '100%',
      },
    },
    contained: {
      boxShadow: 'none',
      '&:hover': {
        boxShadow: 'none',
      },
      backgroundColor: '#F5F7FA',
    },
    containedSecondary: {
      color: theme.palette.common.white,
    },
    containedPrimary: {
      color: theme.palette.common.white,
    },
    outlined: {
      color: theme.palette.text.primary,
      borderColor: '#A3ADC0',
      backgroundColor: theme.palette.common.white,
      padding: '12px 16px',
      '&:hover': {
        backgroundColor: '#F8F9FA',
      },
    },
    outlinedPrimary: {
      color: theme.palette.text.primary,
      '&:hover': {
        backgroundColor: '#F8F9FA',
      },
    },
    outlinedSecondary: {
      color: theme.palette.text.primary,
      '&:hover': {
        backgroundColor: '#F8F9FA',
      },
    },
    text: {
      padding: '12px 16px',
    },
    label: {
      textTransform: 'capitalize',
      gap: '8px',
      lineHeight: '100%',
    },
    sizeSmall: {
      padding: '6px 8px',
      minWidth: 64,
      fontSize: theme.typography.pxToRem(14),
    },
    sizeLarge: {
      padding: '14px 24px',
      fontSize: theme.typography.pxToRem(18),
    },
  },
  MuiIconButton: {
    root: {
      color: theme.palette.text.primary,
      backgroundColor: 'transparent',
      '&:hover': {
        backgroundColor: 'transparent',
      },
      '&$disabled': {
        backgroundColor: 'transparent',
      },
      '&$active': {
        backgroundColor: 'transparent',
      },
      '&$focus': {
        backgroundColor: 'transparent',
      },
    },
  },
  MuiOutlinedInput: {
    root: {
      '&.Mui-focused': {
        '& .MuiOutlinedInput-notchedOutline': {
          border: `2px solid #364754`,
        },
      },
    },
    notchedOutline: {
      borderColor: '#ADBBB9',
    },
  },
  MuiTooltip: {
    tooltip: {
      fontSize: '1em',
    },
  },
  MuiDivider: {
    root: {
      width: '100%',
    },
  },
  MuiLink: {
    root: {
      cursor: 'pointer',
    },
  },
  MuiTabs: {
    root: {
      minHeight: 'initial',
    },
    scrollButtons: {
      width: 'initial',
    },
  },
  MuiTab: {
    root: {
      background: '#F5F7FA',
      marginRight: '8px',
      fontSize: '16px',
      padding: '16px',
      lineHeight: '1',
      textTransform: 'capitalize',
      fontFamily: theme.typography.fontFamily,
      color: theme.palette.text.primary,
      minWidth: 'initial !important',
      minHeight: 'initial !important',
      '&.Mui-selected': {
        color: theme.palette.text.primary,
        background: '#F5F7FA',
      },
      [theme.breakpoints.down(767)]: {
        fontSize: '13px',
        minWidth: '100px',
        padding: '10px 20px',
      },
    },
    textColorInherit: {
      color: theme.palette.text.primary,
      opacity: 1,
    },
  },
  MuiListItemText: {
    primary: {
      fontWeight: 500,
    },
  },
  MuiSelect: {
    root: {
      height: '48px',
      padding: '15.5px 10px',
    },
  },
  MuiLinearProgress: {
    root: {
      height: '4px',
      borderRadius: 4,
    },
    colorPrimary: {
      backgroundColor: theme.palette.grey[200],
    },
    colorSecondary: {
      backgroundColor: theme.palette.grey[200],
    },
    bar: {
      borderRadius: 4,
    },
  },
  MuiTableCell: {
    stickyHeader: {
      backgroundColor: '#F5F7FA',
    },
  },
  MuiStepIcon: {
    root: {
      color: 'white', // inactive circle
      '&$active': {
        color: '#166472', // active
        '& $text': {
          fill: 'white', // active *text* color
        },
      },
      '&$completed': {
        color: '#166472', // completed
      },
    },
    active: {
      color: '#166472', // active circle
    },
    completed: {
      color: '#166472', // completed circle
    },
  },
  MuiFormHelperText: {
    root: {
      width: '100%',
      whiteSpace: 'normal',
    },
    contained: {
      marginLeft: 4,
      marginRight: 4,
    },
  },
  MuiSwitch: {
    switchBase: {
      '&$disabled': {
        '& + $track': {
          backgroundColor: '#E0E2E5',
        },
      },
    },
    colorPrimary: {
      '&$disabled': {
        '& + $track': {
          backgroundColor: '#E0E2E5',
        },
      },
    },
    colorSecondary: {
      '&$disabled': {
        '& + $track': {
          backgroundColor: '#E0E2E5',
        },
      },
    },
  },
};

export { theme as customMuiTheme };
